import format from 'date-fns/format';

import { FolderItemStatus, FolderItemType } from '@alucio/aws-beacon-amplify/src/models';
import type { FolderORM } from 'src/types/types';

export const SHARED_FOLDER_UPDATES_LOOKBACK_DAYS = 14;

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export type SharedFolderUpdateNotification = {
  id: string;
  dateKey: string;
  latestUpdatedAt: string;
  folders: FolderORM[];
}

type FolderUpdateEntry = {
  folder: FolderORM;
  updatedAt: string;
  updatedAtMs: number;
}

type GroupedFolderUpdate = {
  folder: FolderORM;
  latestUpdatedAt: string;
  latestUpdatedAtMs: number;
}

const getTimestamp = (date?: string | null): number => {
  if (!date) return 0;

  const timestamp = new Date(date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const collectFolderUpdateEntries = (
  folder: FolderORM,
  folderById: Map<string, FolderORM>,
  rootFolder: FolderORM = folder,
): FolderUpdateEntry[] => (folder.model.items ?? []).reduce<FolderUpdateEntry[]>((acc, item) => {
  if (item.status === FolderItemStatus.REMOVED) return acc;

  const itemUpdatedAtMs = getTimestamp(item.itemLastUpdatedAt);

  if (itemUpdatedAtMs > 0 && item.itemLastUpdatedAt) {
    acc.push({
      folder: rootFolder,
      updatedAt: item.itemLastUpdatedAt,
      updatedAtMs: itemUpdatedAtMs,
    });
  }

  if (item.type === FolderItemType.FOLDER) {
    const nestedFolder = folderById.get(item.itemId);
    if (nestedFolder) {
      acc.push(...collectFolderUpdateEntries(nestedFolder, folderById, rootFolder));
    }
  }

  return acc;
}, []);

export const getSharedFolderUpdateNotifications = (
  folders: FolderORM[],
  now = new Date(),
): SharedFolderUpdateNotification[] => {
  const cutoff = now.getTime() - (SHARED_FOLDER_UPDATES_LOOKBACK_DAYS * DAY_IN_MS);
  const folderById = new Map(folders.map(folder => [folder.model.id, folder]));
  const groupedUpdates = new Map<string, Map<string, GroupedFolderUpdate>>();

  folders
    .filter(folder => !folder.relations.parentFolderORM)
    .forEach(folder => {
      collectFolderUpdateEntries(folder, folderById)
        .filter(entry => entry.updatedAtMs >= cutoff)
        .forEach(entry => {
          const dateKey = format(new Date(entry.updatedAtMs), 'yyyy-MM-dd');
          const folderUpdates = groupedUpdates.get(dateKey) ?? new Map<string, GroupedFolderUpdate>();
          const currentFolderUpdate = folderUpdates.get(entry.folder.model.id);

          if (!currentFolderUpdate || entry.updatedAtMs > currentFolderUpdate.latestUpdatedAtMs) {
            folderUpdates.set(entry.folder.model.id, {
              folder: entry.folder,
              latestUpdatedAt: entry.updatedAt,
              latestUpdatedAtMs: entry.updatedAtMs,
            });
          }

          groupedUpdates.set(dateKey, folderUpdates);
        });
    });

  return Array.from(groupedUpdates.entries())
    .map(([dateKey, folderUpdates]) => {
      const updates = Array.from(folderUpdates.values())
        .sort((a, b) => b.latestUpdatedAtMs - a.latestUpdatedAtMs);
      const latestUpdate = updates[0];

      return {
        id: `shared-folder-updates-${dateKey}`,
        dateKey,
        latestUpdatedAt: latestUpdate.latestUpdatedAt,
        folders: updates.map(update => update.folder),
      };
    })
    .sort((a, b) => getTimestamp(b.latestUpdatedAt) - getTimestamp(a.latestUpdatedAt));
};
